<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Badge, Drawer } from '@drama/ui'
import {
  Copy,
  Check,
  ZoomIn,
  Code,
  AlertTriangle,
  ExternalLink,
} from '@lucide/vue'
import { videoSupplierCostText } from '../../lib/video-supplier-cost.js'

const props = defineProps({
  shot: { type: Object, required: true },
  execution: { type: Object, required: true },
  selectedDetailTab: { type: String, default: 'prompt' },
  referenceUrls: { type: Object, default: () => ({}) },
  referenceStates: { type: Object, default: () => ({}) },
  referenceErrors: { type: Object, default: () => ({}) },
  sourceStoryHref: { type: String, default: '' },
  evidenceGroups: { type: Array, default: () => [] },
  frozenPassages: { type: Array, default: () => [] },
  selectedScene: { type: Object, default: null },
  predictedCurrency: { type: String, default: null },
})

const emit = defineEmits(['update:selectedDetailTab', 'previewImage'])

const { t } = useI18n()
const copiedKey = ref('')
const techDrawerOpen = ref(false)
const promptMode = ref('actual')
const isPlanned = computed(() => !props.shot.call || promptMode.value === 'planned')
const displayedPrompt = computed(() => isPlanned.value ? props.shot.planned_prompt : props.shot.actual_prompt)
const displayedReferences = computed(() => props.shot.call ? props.shot.actual_references : props.shot.planned_references)

const detailTabs = [
  { key: 'prompt', label: 'storyFilmReview.tabPrompt' },
  { key: 'story', label: 'storyFilmReview.tabStory' },
  { key: 'script', label: 'storyFilmReview.tabScript' },
  { key: 'refs', label: 'storyFilmReview.tabReferences' },
  { key: 'result', label: 'storyFilmReview.tabResult' },
]

async function copyText(text, key) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(String(text))
    copiedKey.value = key
    setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = ''
    }, 2000)
  } catch {
    // fallback
  }
}

function safeLogText(value) {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
}

function safeLogJson(record) {
  return JSON.stringify(record, null, 2)
}

function safeTechnicalDetails(shot) {
  return safeLogJson({ planned_parameters: shot.planned_parameters, preflight: shot.preflight, continuity_dependency: shot.continuity_dependency, call: shot.call, actual_parameters: shot.actual_parameters, predicted_cost: shot.predicted_cost, attempts: shot.attempts })
}

function referenceRole(role) {
  return typeof role === 'string' ? role : t('storyFilm.executionLog.referenceRoleUnknown')
}

function referenceFrameHash(reference) {
  if (reference?.matched !== true || reference?.source !== 'previous_final_cut_frame') return null
  return typeof reference.sha256 === 'string' && /^[a-f0-9]{64}$/i.test(reference.sha256) ? reference.sha256.toLowerCase() : null
}

function speakerLabel(id) {
  if (id === 'narrator') return t('storyFilm.executionLog.narrator')
  if (id === 'player') return t('storyFilm.executionLog.player')
  const names = props.execution?.character_names
  const name = names?.entries?.[id]?.name ?? names?.[id]
  return (typeof name === 'string' && name) || id || t('storyFilm.executionLog.status_unknown')
}

function callStatus(status) {
  if (status === 'submitting') return t('storyFilm.executionLog.call_submitting')
  if (status === 'submitted') return t('storyFilmReview.callSubmitted')
  const known = ['ok', 'error', 'failed', 'queued', 'running']
  if (known.includes(status)) return t(`storyFilm.executionLog.call_${status}`)
  return t(status == null ? 'storyFilm.executionLog.call_unknown' : 'storyFilm.executionLog.status_unknown')
}

function statusTone(status) {
  if (status === 'failed' || status === 'rejected' || status === 'error') return 'danger'
  if (status === 'media_ready' || status === 'ok') return 'success'
  if (status === 'producing' || status === 'running') return 'info'
  return 'neutral'
}

const promptComparison = computed(() => {
  const planned = props.shot?.planned_prompt
  const actual = props.shot?.actual_prompt
  if (typeof planned !== 'string' || !planned || typeof actual !== 'string' || !actual) return 'unknown'
  return planned === actual ? 'same' : 'different'
})

const readableParameters = computed(() => {
  const params = props.shot.call ? props.shot.actual_parameters : props.shot.planned_parameters
  if (!params || typeof params !== 'object') return []
  return ['model', 'width', 'height', 'frames', 'seed', 'steps', 'turbo', 'ref_image_short_edge']
    .filter(key => params[key] != null)
    .map(key => ({ key, value: typeof params[key] === 'boolean' ? t(`storyFilm.executionLog.${params[key] ? 'yes' : 'no'}`) : safeLogText(String(params[key])) }))
})
</script>

<template>
  <aside class="detail-panel" data-testid="execution-shot-detail">
    <!-- 顶部 Tab 栏 -->
    <div class="detail-tabbar">
      <button
        v-for="tab in detailTabs"
        :key="tab.key"
        type="button"
        class="tab-btn"
        :class="{ 'is-active': selectedDetailTab === tab.key }"
        @click="emit('update:selectedDetailTab', tab.key)"
      >
        {{ t(tab.label) }}
      </button>
    </div>

    <!-- Tab 1: 提示词 (Prompt) -->
    <div v-show="selectedDetailTab === 'prompt'" class="detail-section">
      <header class="section-title-row">
        <h4>{{ isPlanned ? t('storyFilmReview.plannedPrompt') : (promptComparison === 'same' ? '计划 = 实发' : '实发提示词') }}（镜 {{ shot.index }}）</h4>
        <Badge :variant="promptComparison === 'same' ? 'success' : 'neutral'" size="sm">
          {{ shot.call ? (promptComparison === 'same' ? '逐字相同' : '已留存') : t('storyFilmReview.notSent') }} · {{ displayedPrompt?.length ?? 0 }} 字符
        </Badge>
      </header>

      <div class="sub-tabbar">
        <button type="button" class="sub-tab" :disabled="!shot.call" :class="{ 'is-active': !isPlanned }" @click="promptMode = 'actual'">实发请求</button>
        <button type="button" class="sub-tab" :class="{ 'is-active': isPlanned }" @click="promptMode = 'planned'">计划稿</button>
        <Button
          v-if="displayedPrompt"
          size="sm"
          variant="ghost"
          class="copy-btn-mini"
          @click="copyText(displayedPrompt, 'prompt')"
        >
          <Check v-if="copiedKey === 'prompt'" :size="12" class="is-ok" />
          <Copy v-else :size="12" />
          <span>{{ t(copiedKey === 'prompt' ? 'storyFilmReview.copiedPrompt' : 'storyFilmReview.copyPrompt') }}</span>
        </Button>
      </div>

      <div class="prompt-box">
        <div class="prompt-content">
          {{ safeLogText(displayedPrompt) }}
        </div>
      </div>

      <!-- 负向约束条 -->
      <div v-if="shot.negative_prompt_planned" class="negative-row">
        <div class="negative-row__label">
          <AlertTriangle :size="13" class="warn-icon" />
          <span>负向约束 · 计划侧</span>
          <span class="hint-text">({{ shot.negative_prompt_planned.length }} 字符)</span>
        </div>
        <Button size="sm" variant="ghost" class="copy-btn-mini" @click="copyText(shot.negative_prompt_planned, 'neg_prompt')">
          <Check v-if="copiedKey === 'neg_prompt'" :size="12" class="is-ok" />
          <Copy v-else :size="12" />
        </Button>
      </div>
      <div v-if="shot.negative_prompt_planned" class="negative-box">
        {{ safeLogText(shot.negative_prompt_planned) }}
      </div>
    </div>

    <!-- Tab 2: 故事原著 (Story Evidence) -->
    <div v-show="selectedDetailTab === 'story'" class="detail-section">
      <header class="section-title-row">
        <h4>来源证据<span class="hint-text">({{ evidenceGroups.length }} 段)</span></h4>
        <a v-if="sourceStoryHref" :href="sourceStoryHref" target="_blank" rel="noopener" class="link-mini">
          {{ t('storyFilmReview.liveStoryLink') }}
          <ExternalLink :size="11" />
        </a>
      </header>

      <div v-if="execution.source_document" class="evidence-box">
        <h4>{{ t('storyFilmReview.sourceDocument') }}</h4>
        <p>{{ execution.source_document.path }}</p>
        <code>SHA-256 {{ execution.source_document.sha256 }}</code>
        <ul><li v-for="note in execution.review_notes" :key="note">{{ note }}</li></ul>
        <details>
          <summary>{{ t('storyFilmReview.sourceDocument') }}</summary>
          <pre class="tech-pre">{{ execution.source_document.text }}</pre>
        </details>
      </div>
      <div v-if="evidenceGroups.length" class="evidence-box">
        <div v-for="(entry, index) in evidenceGroups" :key="index" class="evidence-item">
          <span class="evidence-item__who">{{ speakerLabel(entry.speaker) }}</span>
          <span class="evidence-item__txt">{{ safeLogText(entry.text) }}</span>
        </div>
      </div>
      <p v-else class="empty-hint">{{ t('storyFilm.executionLog.noEvidence') }}</p>

      <!-- 冻结原故事快照折叠 -->
      <details v-if="execution.source_material_status === 'verified_submission_snapshot' && execution.source_material" class="sub-details" data-testid="execution-frozen-source">
        <summary class="sub-details__summary">
          <span>{{ t('storyFilmReview.frozenSource') }} · {{ frozenPassages.length }} 段</span>
        </summary>
        <div class="evidence-box sub-evidence-box">
          <div v-for="(passage, index) in frozenPassages" :key="passage.segment_id ?? index" class="evidence-item">
            <span class="evidence-item__who">{{ speakerLabel(passage.speaker_id) }}</span>
            <span class="evidence-item__txt">{{ safeLogText(passage.text) }}</span>
          </div>
        </div>
      </details>
    </div>

    <!-- Tab 3: 剧本分镜 (Screenplay) -->
    <div v-show="selectedDetailTab === 'script'" class="detail-section">
      <header class="section-title-row">
        <h4>剧本分镜</h4>
        <Badge :variant="selectedScene ? 'success' : 'neutral'" size="sm">
          {{ selectedScene ? safeLogText(selectedScene.title) : '历史未留存' }}
        </Badge>
      </header>

      <div v-if="selectedScene" class="screenplay-content">
        <p v-if="selectedScene.summary_en" class="screenplay-summary">{{ safeLogText(selectedScene.summary_en) }}</p>
        <div v-for="(beat, index) in selectedScene.beats ?? []" :key="index" class="beat-card">
          <div class="beat-action"><b>动作 {{ index + 1 }}：</b>{{ safeLogText(beat.action) }}</div>
          <div v-for="(line, lineIndex) in beat.dialogue ?? []" :key="lineIndex" class="beat-line">
            <span class="beat-speaker">{{ speakerLabel(line.speaker_id) }}：</span>
            <span class="beat-text">{{ safeLogText(line.text) }}</span>
          </div>
        </div>
      </div>
      <p v-else class="empty-hint">{{ t('storyFilm.executionLog.screenplayNotRecorded') }}</p>
    </div>

    <!-- Tab 4: 角色参考图 (References) -->
    <div v-show="selectedDetailTab === 'refs'" class="detail-section" data-testid="execution-actual-references">
      <header class="section-title-row">
        <h4>角色参考图</h4>
        <span class="hint-text">{{ t(shot.call ? 'storyFilmReview.actualReferences' : 'storyFilmReview.plannedReferences') }} · {{ displayedReferences?.length ?? 0 }}</span>
      </header>

      <p v-if="!shot.call && shot.continuity_dependency" class="hint-text">{{ t('storyFilmReview.runtimeFramePending') }}</p>
      <div v-if="displayedReferences?.length" class="refs-flow">
        <div v-for="(reference, index) in displayedReferences" :key="index" class="reference-card">
          <div
            class="ref-thumb-wrap"
            @click="reference?.matched === true && referenceUrls[`${shot.index}:${index}`] && emit('previewImage', referenceUrls[`${shot.index}:${index}`])"
          >
            <img
              v-if="reference?.matched === true && referenceUrls[`${shot.index}:${index}`]"
              :src="referenceUrls[`${shot.index}:${index}`]"
              :alt="referenceRole(reference.role)"
              class="ref-thumb-img"
            />
            <div v-else class="ref-thumb-empty">
              <span>{{ reference?.matched === true ? '加载中…' : '未匹配' }}</span>
            </div>
            <div v-if="reference?.matched === true && referenceUrls[`${shot.index}:${index}`]" class="ref-thumb-zoom">
              <ZoomIn :size="16" />
            </div>
          </div>

          <div class="reference-card__meta">
            <b>{{ reference?.matched === true ? (reference.name ?? referenceRole(reference.role)) : t('storyFilm.executionLog.referenceRoleUnknown') }}</b>
            <span class="hint-text">按镜鉴权代理读取</span>
            <Badge :variant="reference?.matched === true ? 'success' : 'neutral'" size="sm">
              {{ shot.call ? t(reference?.matched === true ? 'storyFilm.executionLog.referenceRecordMatched' : 'storyFilm.executionLog.referenceRecordUnverified') : t('storyFilmReview.notSent') }}
            </Badge>
            <template v-if="reference?.matched === true && reference.source === 'previous_final_cut_frame'">
              <span class="continuation-tag">{{ t('storyFilm.executionLog.referenceContinuation') }}</span>
              <code v-if="referenceFrameHash(reference)" class="hash-tag">SHA-256 {{ referenceFrameHash(reference).slice(0, 10) }}...</code>
            </template>
          </div>
        </div>
      </div>
      <p v-else class="empty-hint">{{ t('storyFilm.executionLog.actualReferencesEmpty') }}</p>
    </div>

    <!-- Tab 5: 结果与成本 (Result & Cost) -->
    <div v-show="selectedDetailTab === 'result'" class="detail-section">
      <header class="section-title-row">
        <h4>{{ shot.call ? '实发参数与成本' : t('storyFilmReview.plannedParameters') }}</h4>
        <Badge :variant="statusTone(shot.call?.status)" size="sm">{{ callStatus(shot.call?.status) }}</Badge>
      </header>

      <div v-if="readableParameters.length" class="param-tags-row">
        <span v-for="parameter in readableParameters" :key="parameter.key" class="param-badge">
          {{ t(parameter.key === 'ref_image_short_edge' ? 'storyFilmReview.refShortEdge' : `storyFilm.executionLog.param_${parameter.key}`) }}: {{ parameter.value }}
        </span>
      </div>

      <div class="cost-grid-box">
        <div class="cost-box">
          <span class="cost-box__label">预测成本</span>
          <b>{{ shot.predicted_cost?.points != null ? shot.predicted_cost.points + ' 积分' : '—' }}</b>
          <span v-if="shot.predicted_cost?.cost_micros != null" class="cost-box__sub">
            {{ (Number(shot.predicted_cost.cost_micros) / 1_000_000).toFixed(6) }} {{ shot.predicted_cost.currency ?? 'CNY' }}
          </span>
        </div>
        <div class="cost-box">
          <span class="cost-box__label">{{ t('storyFilmReview.supplierCostLabel') }}</span>
          <b class="is-accent">{{ videoSupplierCostText(shot.call, t) }}</b>
          <span class="cost-box__sub">供应商记账实耗</span>
        </div>
      </div>

      <dl class="kv-list">
        <dt>调用</dt>
        <dd>{{ callStatus(shot.call?.status) }} · {{ shot.call?.call_id ?? '无 ID' }} · {{ shot.call?.duration_ms != null ? (shot.call.duration_ms / 1000).toFixed(1) + 's' : '—' }}</dd>
      </dl>

      <div class="action-buttons-row">
        <Button size="sm" variant="secondary" @click="techDrawerOpen = true">
          <Code :size="13" />
          <span>查看原始 request_json / 技术资料</span>
        </Button>
      </div>
    </div>

    <!-- 技术细节抽屉 (Drawer) -->
    <Drawer :open="techDrawerOpen" title="原始记录与技术资料" @close="techDrawerOpen = false">
      <div class="tech-drawer-body">
        <div class="tech-drawer-head">
          <span>镜 {{ shot.index }} 原始参数结构</span>
          <Button size="sm" variant="ghost" @click="copyText(safeTechnicalDetails(shot), 'drawer_tech')">
            <Check v-if="copiedKey === 'drawer_tech'" :size="13" class="is-ok" />
            <Copy v-else :size="13" />
            <span>{{ t(copiedKey === 'drawer_tech' ? 'storyFilmReview.copiedPrompt' : 'visualGame.detail.copy') }}</span>
          </Button>
        </div>
        <pre class="tech-pre">{{ safeTechnicalDetails(shot) }}</pre>
      </div>
    </Drawer>
  </aside>
</template>

<style scoped>
.detail-panel {
  display: flex;
  flex-direction: column;
  background: var(--drama-color-surface);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  min-width: 0;
  box-shadow: var(--drama-shadow-xs);
}

.detail-tabbar {
  display: flex;
  gap: 4px;
  padding: 8px 10px;
  border-bottom: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface);
  position: sticky;
  top: 0;
  z-index: 2;
  flex-wrap: wrap;
}

.tab-btn {
  border: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-sm);
  padding: 6px 12px;
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text-muted);
  cursor: pointer;
  transition: all 0.15s ease;
}

.tab-btn.is-active {
  background: var(--drama-color-surface);
  color: var(--drama-color-text);
  border-color: var(--drama-color-border-strong);
  font-weight: 600;
}

.detail-section {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title-row h4 {
  margin: 0;
  font-size: var(--drama-font-size-sm);
  font-weight: 600;
}

.sub-tabbar {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sub-tab {
  border: 1px solid var(--drama-color-border);
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-xs, 4px);
  padding: 3px 8px;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  cursor: pointer;
}

.sub-tab.is-active {
  background: var(--drama-color-surface);
  color: var(--drama-color-text);
  font-weight: 600;
  border-color: var(--drama-color-border-strong);
}

.copy-btn-mini {
  margin-left: auto;
  font-size: var(--drama-font-size-xs);
}

.prompt-box {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  background: var(--drama-color-surface-alt);
  padding: 10px 12px;
  font-size: var(--drama-font-size-sm);
  line-height: 1.6;
  font-family: var(--drama-font-family-mono);
  user-select: text;
}

.prompt-content {
  white-space: pre-wrap;
  word-break: break-word;
}

.negative-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 6px 10px;
  background: var(--drama-color-surface-alt);
  font-size: var(--drama-font-size-xs);
}

.negative-row__label {
  display: flex;
  align-items: center;
  gap: 6px;
}

.warn-icon {
  color: var(--drama-color-warning);
}

.negative-box {
  font-size: var(--drama-font-size-sm);
  font-family: var(--drama-font-family-mono);
  color: var(--drama-color-warning);
  background: var(--drama-color-surface);
  border: 1px dashed var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 8px 10px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 来源证据 */
.evidence-box {
  max-height: 240px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: var(--drama-font-size-sm);
  line-height: 1.6;
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-sm);
  padding: 10px 12px;
}

.evidence-item {
  display: flex;
  gap: 8px;
}

.evidence-item__who {
  flex: none;
  width: 50px;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  font-weight: 600;
}

.evidence-item__txt {
  color: var(--drama-color-text);
  word-break: break-word;
}

.sub-details {
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  overflow: hidden;
}

.sub-details__summary {
  padding: 8px 10px;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  cursor: pointer;
  background: var(--drama-color-surface-alt);
}

.sub-evidence-box {
  background: var(--drama-color-surface);
  border-radius: 0;
}

/* 剧本分镜 */
.screenplay-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.screenplay-summary {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  font-style: italic;
  margin: 0;
}

.beat-card {
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-sm);
  padding: 8px 10px;
  font-size: var(--drama-font-size-sm);
}

.beat-action {
  margin-bottom: 6px;
}

.beat-line {
  margin-left: 10px;
  font-size: var(--drama-font-size-sm);
}

.beat-speaker {
  font-weight: 600;
  color: var(--drama-color-primary);
}

/* 角色参考图 */
.refs-flow {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.reference-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 10px;
  background: var(--drama-color-surface-alt);
}

.ref-thumb-wrap {
  position: relative;
  width: 96px;
  height: 108px;
  border: 1px dashed var(--drama-color-border-strong);
  border-radius: var(--drama-radius-sm);
  overflow: hidden;
  background: var(--drama-color-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}

.ref-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ref-thumb-empty {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  text-align: center;
}

.ref-thumb-zoom {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.ref-thumb-wrap:hover .ref-thumb-zoom {
  opacity: 1;
}

.reference-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  font-size: var(--drama-font-size-xs);
}

.continuation-tag {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-info);
  padding: 2px 6px;
  background: color-mix(in srgb, var(--drama-color-info) 10%, transparent);
  border-radius: var(--drama-radius-xs, 3px);
  display: inline-block;
  align-self: flex-start;
}

.hash-tag {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  padding: 2px 6px;
  background: var(--drama-color-surface);
  border-radius: var(--drama-radius-xs, 3px);
  display: inline-block;
  align-self: flex-start;
}

/* 结果与成本 */
.param-tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.param-badge {
  padding: 4px 10px;
  background: var(--drama-color-surface-alt);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-full);
  font-size: var(--drama-font-size-xs);
  line-height: 1.35;
}

.cost-grid-box {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.cost-box {
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-sm);
  padding: 8px 10px;
  background: var(--drama-color-surface-alt);
  font-size: var(--drama-font-size-xs);
}

.cost-box__label {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.cost-box b {
  display: block;
  font-size: var(--drama-font-size-sm);
  margin-top: 2px;
}

.cost-box b.is-accent {
  color: var(--drama-color-primary);
}

.cost-box__sub {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
}

.kv-list {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 4px 8px;
  font-size: var(--drama-font-size-sm);
  margin: 0;
}

.kv-list dt {
  color: var(--drama-color-text-muted);
}

.kv-list dd {
  margin: 0;
  word-break: break-all;
}

.action-buttons-row {
  display: flex;
  gap: 8px;
  margin-top: 6px;
}

/* 抽屉内容 */
.tech-drawer-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tech-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--drama-font-size-sm);
  font-weight: 600;
}

.tech-pre {
  margin: 0;
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
  background: var(--drama-color-surface-alt);
  padding: 12px;
  border-radius: var(--drama-radius-sm);
  max-height: 60vh;
  overflow-y: auto;
}

.hint-text {
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-text-muted);
  line-height: 1.45;
}

.empty-hint {
  font-size: var(--drama-font-size-sm);
  color: var(--drama-color-text-muted);
  text-align: center;
  padding: 14px;
  margin: 0;
}

.link-mini {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--drama-font-size-xs);
  color: var(--drama-color-info);
  text-decoration: underline;
}

.is-ok {
  color: var(--drama-color-success);
}
</style>
